import {
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { formatDistance, fromUnixTime } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  shareReplay,
  Subject,
  takeUntil,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { checkCanCancelJob } from 'server/shared/helpers/checkCanCancelJob';
import { getJobCounts } from 'server/shared/helpers/getJobCounts';
import {
  DispoJobStatus,
  getJobStatusArrayForDispo,
} from 'server/shared/helpers/getJobStatusForDispo';
import { filterOverviewInvitees } from 'server/shared/helpers/job/filterOverviewInvitees';
import { getNewUnixTimestamp } from 'server/shared/helpers/removeTimeZoneOffset';
import { Invitee } from 'server/shared/models/invitee';
import { Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { DispoUser } from 'server/shared/models/user';
import { JobRequestService } from 'src/app/services/job-request.service';
import { UserService } from 'src/app/services/user.service';
import { filterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { Column } from '../../../helpers/job/jobColumns';
import { CustomerService } from '../../services/customer.service';
import { JobEditService } from '../../services/job-edit.service';
import { TranslationService } from '../../services/translation.service';

const statusOrder = [InviteeStatusEnum.BOOKED, InviteeStatusEnum.CANCELED];
function sortByStatusOrder(a: Invitee, b: Invitee) {
  return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
}

@Component({
  selector: 'app-dispo-job-list-item',
  templateUrl: './dispo-job-list-item.component.html',
  styleUrls: [
    './dispo-job-list-item.component.scss',
    '../../../stylesheets/components/atoms/jobs-list-view.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispoJobListItemComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  i18n = this.translationService.getTranslations('jobs');
  job$ = new BehaviorSubject<Job | undefined>(undefined);
  @Input() set job(job: Job | undefined) {
    this.job$.next(job);
  }
  get job(): Job | undefined {
    return this.job$.getValue();
  }

  private loggedInUserInput$ = new BehaviorSubject<DispoUser | undefined>(
    undefined,
  );
  loggedInUser$ = this.loggedInUserInput$.pipe(takeUntil(this.destroyed$));

  @Input() set loggedInUser(loggedInUser: DispoUser | undefined | null) {
    this.loggedInUserInput$.next(loggedInUser ?? undefined);
  }
  get loggedInUser() {
    return this.loggedInUserInput$.value;
  }

  private compactInput$ = new BehaviorSubject<boolean | undefined>(undefined);
  compact$ = this.compactInput$.pipe(takeUntil(this.destroyed$));

  @Input() set compact(compact: boolean | undefined) {
    this.compactInput$.next(compact);
  }
  get compact() {
    return this.compactInput$.value;
  }

  withDate$ = new BehaviorSubject<boolean>(false);
  @Input() set withDate(value: boolean) {
    this.withDate$.next(value);
  }
  get withDate(): boolean {
    return this.withDate$.value;
  }

  columns$ = new BehaviorSubject<Column[]>([]);
  @Input() set columns(columns: Column[]) {
    this.columns$.next(columns);
  }
  get columns(): Column[] {
    return this.columns$.value;
  }

  jobStatusEnum = JobStatusEnum;
  inviteeStatusEnum = InviteeStatusEnum;

  dispoCustomer$ = this.customerService.readyDispoCustomer$.pipe(
    takeUntil(this.destroyed$),
  );

  customerOfJob$ = combineLatest([this.job$, this.dispoCustomer$]).pipe(
    takeUntil(this.destroyed$),
    map(([job, customer]) => {
      if (!job || !customer) return;
      if (job.customer.id === customer._id) {
        return customer;
      } else {
        return Customers.findOne(job.customer.id);
      }
    }),
  );

  jobCounts$ = combineLatest([
    this.job$,
    this.dispoCustomer$,
    this.customerService.sharedPoolCustomers$,
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([job, customer, sharedPoolCustomers]) =>
      getJobCounts(job, customer, sharedPoolCustomers),
    ),
  );

  dispoJobStatusArray$ = combineLatest([
    this.job$,
    this.jobCounts$,
    this.dispoCustomer$,
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([job, counts, customer]) => {
      if (!job || !counts || !customer) return;

      return getJobStatusArrayForDispo(job, counts, customer);
    }),
    shareReplay(1),
  );

  canCancel$ = combineLatest([
    this.job$.pipe(filter(filterIsNotEmpty)),
    this.dispoCustomer$,
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([job, customer]) => checkCanCancelJob(job, customer)),
  );

  overviewInvitees$ = this.job$.pipe(
    filter(filterIsNotEmpty),
    takeUntil(this.destroyed$),
    map((job) => {
      return filterOverviewInvitees(job).sort(sortByStatusOrder);
    }),
    shareReplay(1),
  );

  transferCounts$ = combineLatest([
    this.job$.pipe(filter(filterIsNotEmpty)),
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(([job, customer]) => {
      const isOwnJob = customer && job.customer.id === customer._id;
      if (
        !job.transfers?.length ||
        !isOwnJob ||
        (!job.allowExternalInvitees && job.allowExternalInvitees !== undefined)
      ) {
        return undefined;
      }
      const pendingAutoTransfers = job.transfers
        .filter(
          (t) =>
            t.transferAt &&
            !t.transferredAt &&
            t.auto &&
            !t.conditionallyBlocked,
        )
        .map((t) => t.transferAt)
        // second filter just for typescript, is already covered in the filter above
        .filter(filterIsNotEmpty);
      let nextAutoTransfer = pendingAutoTransfers.sort((a, b) => a - b)[0];
      if (nextAutoTransfer && nextAutoTransfer < getNewUnixTimestamp()) {
        nextAutoTransfer = getNewUnixTimestamp();
      }
      return {
        total: job.transfers.length,
        transferred: job.transfers.filter(
          (t) => t.transferredAt && !t.declinedAt,
        ).length,
        declined: job.transfers.filter((t) => t.declinedAt).length,
        auto: pendingAutoTransfers.length,
        nextAutoTransfer,
      };
    }),
  );

  transferredToMeText$ = combineLatest([
    this.job$.pipe(filter(filterIsNotEmpty)),
    this.customerService.readyDispoCustomer$,
    this.userService.readyDispo$,
  ]).pipe(
    map(([job, loggedInCustomer, loggedInDispo]) => {
      const transfer = job?.transfers?.find(
        (transfer) => transfer.targetCustomer.id === loggedInCustomer._id,
      );
      if (!transfer || !transfer.transferredAt) {
        return;
      }
      if (transfer.declinedAt) {
        return (
          job.customer.displayName +
          ' ' +
          (this.i18n?.declined || 'abgelehnt') +
          ' ' +
          formatDistance(fromUnixTime(transfer.declinedAt), new Date(), {
            addSuffix: true,
            locale: loggedInDispo.locale === 'en' ? enUS : de,
          })
        );
      }
      return (
        job.customer.displayName +
        ': ' +
        (this.i18n?.transferred || 'transferiert') +
        ' ' +
        formatDistance(fromUnixTime(transfer.transferredAt), new Date(), {
          addSuffix: true,
          locale: loggedInDispo.locale === 'en' ? enUS : de,
        })
      );
    }),
  );

  constructor(
    private customerService: CustomerService,
    private ngZone: NgZone,
    private router: Router,
    private translationService: TranslationService,
    public jobEditService: JobEditService,
    private userService: UserService,
    public jobRequestService: JobRequestService,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  navigateToMultiDayEdit(job: Job) {
    if (job.multiDayId) {
      this.router.navigate(['/multiday-job', job.multiDayId]).then();
    }
  }

  readonly DispoJobStatus = DispoJobStatus;

  hasOpenHoursToApprove$ = this.job$.pipe(
    takeUntil(this.destroyed$),
    map((job) => {
      return !!job?.invitees?.some((invitee) =>
        invitee.timeTrackings?.some(
          (timeTracking) =>
            timeTracking.toBeApprovedAt && !timeTracking.approvedAt,
        ),
      );
    }),
  );
}
